import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentEvents,
  AutopilotUpdatedEvent,
  GetAutopilotResponse,
  ListAutopilotsResponse,
  AutopilotSummary,
  GetDailyStandupSettingsResponse,
  UpdateDailyStandupSettingsResponse,
} from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import api from '../../api';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as SunSettingIcon } from '../../icons/sun-setting.svg';
import { ReactComponent as ChevronDownIcon } from '../../icons/chevron-down.svg';
import { ReactComponent as TrashOutlineIcon } from '../../icons/trash-outline.svg';
import { ReactComponent as CloseXIcon } from '../../icons/close-x.svg';
import './routine-detail-view.scss';

interface RoutineDetailViewProps {
  autopilotId: string;
  routineName?: string;
  onBack: () => void;
}

/**
 * Parse bullet-point instructions into structured items.
 * Expected format from the AI:
 * "* At 9am every Thursday I will:\n* Check if...\n* Do something else"
 *
 * The first bullet is the trigger/schedule line.
 * Subsequent bullets are individual instruction steps.
 */
function parseInstructions(raw: string): {
  trigger: string;
  steps: string[];
} {
  const lines = raw
    .split('\n')
    .map((l) => l.replace(/^[*-]\s*/, '').trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return { trigger: '', steps: [] };
  }

  return {
    trigger: lines[0],
    steps: lines.slice(1),
  };
}

/**
 * Render text with **bold** markdown into React elements.
 */
function renderBoldMarkdown(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

/**
 * Render text with **bold** markdown AND transform "Daily Briefing" mentions
 * into a hoverable inline element that shows other contributing routines.
 */
function renderWithDailyBriefingLink(
  text: string,
  otherBriefingRoutines: AutopilotSummary[],
  onOpenModal: () => void,
): React.ReactNode {
  // First apply bold markdown splitting
  const boldParts = text.split(/(\*\*[^*]+\*\*)/);
  return boldParts.map((part, i) => {
    const isBold = part.startsWith('**') && part.endsWith('**');
    const inner = isBold ? part.slice(2, -2) : part;

    // Check if this part mentions "Daily Briefing" (case-insensitive)
    const briefingRegex = /(Daily Briefing)/i;
    if (briefingRegex.test(inner)) {
      const segments = inner.split(briefingRegex);
      const rendered = segments.map((seg, j) => {
        if (briefingRegex.test(seg)) {
          return (
            <DailyBriefingLink
              key={`${i}-${j}`}
              otherRoutines={otherBriefingRoutines}
              onOpenModal={onOpenModal}
            />
          );
        }
        return seg;
      });
      return isBold ? (
        <strong key={i}>{rendered}</strong>
      ) : (
        <React.Fragment key={i}>{rendered}</React.Fragment>
      );
    }

    if (isBold) {
      return <strong key={i}>{inner}</strong>;
    }
    return part;
  });
}

const MAX_SHOWN_ROUTINES = 4;

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

const DailyBriefingModal: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [hour, setHour] = useState('07');
  const [minute, setMinute] = useState('30');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchSettings = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetDailyStandupSettingsResponse>(
          '/daily-standup/settings',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled && res.data.success && res.data.settings) {
          const [h, m] = res.data.settings.callTime.split(':');
          setHour(h);
          setMinute(m);
        }
      } catch {
        // use defaults
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    fetchSettings();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      await api.put<UpdateDailyStandupSettingsResponse>(
        '/daily-standup/settings',
        { callTime: `${hour}:${minute}` },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onClose();
    } catch (err) {
      console.error('[DailyBriefingModal] save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='daily-briefing-modal-overlay' onClick={onClose}>
      <div
        className='daily-briefing-modal'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='daily-briefing-modal-header'>
          <SunSettingIcon className='daily-briefing-modal-icon' />
          <h2 className='daily-briefing-modal-title'>
            What's the Daily Briefing?
          </h2>
        </div>

        <div className='daily-briefing-modal-body'>
          <p>
            Get your personalised update to set you up for the day. Your
            briefing includes summaries from your Routines, as well as any
            Projects or Tasks that need your attention.
          </p>
          <p>I'll send it by email, along with a summary on WhatsApp.</p>
        </div>

        {loaded && (
          <div className='daily-briefing-modal-time'>
            <span className='daily-briefing-modal-time-label'>
              Delivery time
            </span>
            <div className='daily-briefing-modal-time-pickers'>
              <div className='daily-briefing-modal-select-wrapper'>
                <select value={hour} onChange={(e) => setHour(e.target.value)}>
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className='daily-briefing-modal-select-chevron' />
              </div>
              <div className='daily-briefing-modal-select-wrapper'>
                <select
                  value={minute}
                  onChange={(e) => setMinute(e.target.value)}
                >
                  {MINUTES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className='daily-briefing-modal-select-chevron' />
              </div>
            </div>
          </div>
        )}

        <div className='daily-briefing-modal-actions'>
          <button
            type='button'
            className='daily-briefing-modal-btn-cancel'
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type='button'
            className='daily-briefing-modal-btn-save'
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DailyBriefingLink: React.FC<{
  otherRoutines: AutopilotSummary[];
  onOpenModal: () => void;
}> = ({ otherRoutines, onOpenModal }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  return (
    <span
      ref={ref}
      className='daily-briefing-link'
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={(e) => {
        e.stopPropagation();
        onOpenModal();
      }}
    >
      <SunSettingIcon className='daily-briefing-link-icon' />
      <span className='daily-briefing-link-text'>Daily Briefing</span>
      {open && (
        <span className='daily-briefing-popover'>
          <span className='daily-briefing-popover-title'>
            Other Routines in your Daily Briefing
          </span>
          {otherRoutines.length > 0 ? (
            <>
              <ul className='daily-briefing-popover-list'>
                {otherRoutines.slice(0, MAX_SHOWN_ROUTINES).map((r) => (
                  <li key={r.id}>{r.name}</li>
                ))}
              </ul>
              {otherRoutines.length > MAX_SHOWN_ROUTINES && (
                <span className='daily-briefing-popover-more'>
                  + {otherRoutines.length - MAX_SHOWN_ROUTINES} others
                </span>
              )}
            </>
          ) : (
            <span className='daily-briefing-popover-empty'>
              This is the only source of information for your Daily Briefing.
            </span>
          )}
          <span className='daily-briefing-popover-edit'>
            Click to edit briefing
          </span>
        </span>
      )}
    </span>
  );
};

const RoutineDetailView: React.FC<RoutineDetailViewProps> = ({
  autopilotId,
  routineName,
  onBack,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [name, setName] = useState(routineName || '');
  const [instructions, setInstructions] = useState('');
  const [active, setActive] = useState(true);
  const [contributesToDailyBriefing, setContributesToDailyBriefing] =
    useState(false);
  const [otherBriefingRoutines, setOtherBriefingRoutines] = useState<
    AutopilotSummary[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [isSampleRoutine, setIsSampleRoutine] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [briefingModalOpen, setBriefingModalOpen] = useState(false);

  const fetchAutopilot = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetAutopilotResponse>(
        `/autopilots/${autopilotId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setName(res.data.autopilot.name);
        setInstructions(res.data.autopilot.instructions);
        setActive(res.data.autopilot.active);
        setContributesToDailyBriefing(
          res.data.autopilot.contributes_to_daily_briefing,
        );
      }
    } catch (err) {
      console.error('[RoutineDetailView] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [autopilotId, getAccessTokenSilently]);

  useEffect(() => {
    fetchAutopilot();
  }, [fetchAutopilot]);

  // Re-fetch when the backend pushes an update for this autopilot
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  useEffect(() => {
    if (!userChannel) return;

    const handleUpdated = (data: AutopilotUpdatedEvent) => {
      if (data?.autopilotId === autopilotId) {
        fetchAutopilot();
      }
    };

    userChannel.bind(AgentEvents.AUTOPILOT_UPDATED, handleUpdated);
    return () => {
      userChannel.unbind(AgentEvents.AUTOPILOT_UPDATED, handleUpdated);
    };
  }, [userChannel, autopilotId, fetchAutopilot]);

  // Fetch other routines that contribute to the daily briefing (for the popover)
  useEffect(() => {
    let cancelled = false;
    const fetchBriefingRoutines = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<ListAutopilotsResponse>('/autopilots', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data.success) {
          setOtherBriefingRoutines(
            res.data.autopilots.filter(
              (a) =>
                a.contributes_to_daily_briefing &&
                a.active &&
                a.id !== autopilotId,
            ),
          );
          const current = res.data.autopilots.find((a) => a.id === autopilotId);
          if (current) {
            setIsSampleRoutine(current.sample_routine_id !== null);
          }
        }
      } catch (err) {
        // non-critical — popover just won't show
      }
    };
    fetchBriefingRoutines();
    return () => {
      cancelled = true;
    };
  }, [autopilotId, getAccessTokenSilently]);

  const handleArchive = useCallback(async () => {
    if (archiving || isSampleRoutine) return;
    setArchiving(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.patch<{ success: boolean }>(
        `/autopilots/${autopilotId}/archived`,
        { archived: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        onBack();
      }
    } catch (err) {
      console.error('[RoutineDetailView] archive error:', err);
    } finally {
      setArchiving(false);
    }
  }, [archiving, autopilotId, getAccessTokenSilently, isSampleRoutine, onBack]);

  const handleToggleActive = useCallback(async () => {
    if (toggling) return;
    setToggling(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.patch(
        `/autopilots/${autopilotId}/active`,
        { active: !active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setActive(res.data.autopilot.active);
      }
    } catch (err) {
      console.error('[RoutineDetailView] toggle error:', err);
    } finally {
      setToggling(false);
    }
  }, [active, autopilotId, getAccessTokenSilently, toggling]);

  const parsed = parseInstructions(instructions);

  return (
    <div className='routine-detail-view'>
      <button
        type='button'
        className='routine-detail-view-back'
        onClick={onBack}
      >
        <ArrowLeftIcon />
        Back
      </button>

      {loading ? (
        <div className='routine-detail-view-loading'>Loading...</div>
      ) : (
        <div className='routine-detail-view-header'>
          <div className='routine-detail-view-header-top'>
            <span className='routine-detail-view-subtitle'>SETUP ROUTINE</span>
            <div className='routine-detail-view-title-row'>
              <h2 className='routine-detail-view-title'>{name}</h2>
              <div className='routine-detail-view-actions'>
                <div className='routine-detail-view-archive-wrapper'>
                  <button
                    type='button'
                    className='routine-detail-view-archive-btn'
                    onClick={() => setShowArchiveModal(true)}
                    disabled={archiving || isSampleRoutine}
                  >
                    <TrashOutlineIcon />
                  </button>
                  {isSampleRoutine && (
                    <span className='routine-detail-view-archive-tooltip'>
                      Default Routines can&apos;t be archived
                    </span>
                  )}
                </div>
                {active ? (
                  <button
                    type='button'
                    className='routine-detail-view-stop-btn'
                    onClick={handleToggleActive}
                    disabled={toggling}
                  >
                    Stop Routine
                  </button>
                ) : (
                  <button
                    type='button'
                    className='routine-detail-view-start-btn'
                    onClick={handleToggleActive}
                    disabled={toggling}
                  >
                    Start Routine
                  </button>
                )}
              </div>
            </div>
          </div>

          {(parsed.trigger || parsed.steps.length > 0) && (
            <div className='routine-detail-view-instructions'>
              <h3 className='routine-detail-view-instructions-title'>
                What will I do?
              </h3>
              <ul className='routine-detail-view-steps'>
                {parsed.trigger && (
                  <li className='routine-detail-view-step trigger'>
                    <span>{renderBoldMarkdown(parsed.trigger)}</span>
                  </li>
                )}
                {parsed.steps.map((step, i) => (
                  <li key={i} className='routine-detail-view-step'>
                    <span>
                      {renderWithDailyBriefingLink(
                        step,
                        otherBriefingRoutines,
                        () => setBriefingModalOpen(true),
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {showArchiveModal && (
        <div
          className='archive-modal-overlay'
          onClick={() => setShowArchiveModal(false)}
        >
          <div className='archive-modal' onClick={(e) => e.stopPropagation()}>
            <div className='archive-modal-body'>
              <div className='archive-modal-header'>
                <h2 className='archive-modal-title'>Archive this Routine?</h2>
                <button
                  type='button'
                  className='archive-modal-close'
                  onClick={() => setShowArchiveModal(false)}
                >
                  <CloseXIcon />
                </button>
              </div>
              <div className='archive-modal-text'>
                <p>You&apos;re about to archive this Routine.</p>
                <p>
                  Once it&apos;s archived it will be stopped immediately, and if
                  relevant will no longer appear in your Daily Briefing.
                </p>
                <p className='archive-modal-text-bold'>
                  Some actions may be in progress, check your Activity list to
                  remove any in progress actions.
                </p>
              </div>
            </div>
            <div className='archive-modal-actions'>
              <button
                type='button'
                className='archive-modal-btn-cancel'
                onClick={() => setShowArchiveModal(false)}
              >
                Cancel
              </button>
              <button
                type='button'
                className='archive-modal-btn-confirm'
                onClick={() => {
                  setShowArchiveModal(false);
                  handleArchive();
                }}
                disabled={archiving}
              >
                {archiving ? 'Archiving...' : 'Yes, archive'}
              </button>
            </div>
          </div>
        </div>
      )}
      {briefingModalOpen && (
        <DailyBriefingModal onClose={() => setBriefingModalOpen(false)} />
      )}
    </div>
  );
};

export default RoutineDetailView;
